import { useState } from 'react';
import { ArrowLeft, Plus } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  DataGrid,
  FormDialog,
  Input,
  messageFromError,
  monthOnly,
  MonthStepper,
  PageFrame,
  SearchableSelect,
  searchableSource,
  shiftMonth,
  toast,
  unwrapAction,
  useGridConfig,
} from '@eo/shared';

import type { AssignmentItem, AttributedList, BudgetHistoryRow, SupplementItem } from './api/types';
import { budgetFormConfig, supplementFormConfig } from './config/formConfig';
import {
  makeAssignmentsGridConfig,
  makeAttributedListsGridConfig,
  makeBudgetHistoryGridConfig,
  makeSupplementsGridConfig,
} from './config/gridConfig';
import { formatHours } from './helpers';
import type { BudgetValues, ClientDetailContentProps, SupplementValues } from './types';

export function ClientDetailContent({
  data,
  onUpsertBudget,
  onCreateSupplement,
  onRemoveSupplement,
  onUpsertAssignment,
  onRemoveAssignment,
  onQueryOptions,
}: ClientDetailContentProps) {
  const [, setSearchParams] = useSearchParams();
  const [editingBudget, setEditingBudget] = useState(false);
  const [addingSupplement, setAddingSupplement] = useState(false);
  const [assignment, setAssignment] = useState({ userId: '', name: '', fixed: '', variable: '' });
  const [busy, setBusy] = useState(false);

  const capacity = data.capacity;
  const month = data.month;

  const onMonthShift = (delta: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('month', shiftMonth(month, delta));
      return params;
    });
  };

  const run = async (work: () => Promise<void>, failure: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await work();
    } catch (err) {
      toast.error(messageFromError(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const historyConfig = useGridConfig(makeBudgetHistoryGridConfig, undefined);
  const attributionConfig = useGridConfig(makeAttributedListsGridConfig, undefined);
  const supplementsConfig = useGridConfig(makeSupplementsGridConfig, {
    onRemove: (row: SupplementItem) =>
      void run(async () => {
        unwrapAction(
          await onRemoveSupplement({ id: row.id, clientId: capacity!.client.id, month }),
        );
        toast.success('Supplement removed.');
      }, 'Removing the supplement failed. Try again.'),
  });
  const assignmentsConfig = useGridConfig(makeAssignmentsGridConfig, {
    onRemove: (row: AssignmentItem) =>
      void run(async () => {
        unwrapAction(
          await onRemoveAssignment({ id: row.id, clientId: capacity!.client.id, month }),
        );
        toast.success('Assignment removed.');
      }, 'Removing the assignment failed. Try again.'),
  });

  if (!capacity) return <p className="p-6 text-sm text-text-tertiary">No client loaded.</p>;

  const { client, budget } = capacity;
  const assignedStaffIds = new Set(capacity.assignments.map((row) => row.userId));
  const members = data.options.users;
  const memberSearch = searchableSource(members, (q) => onQueryOptions({ source: 'users', ...q }));

  return (
    <div className="flex flex-col">
      <PageFrame
        backLink={
          <Link
            to="/admin?tab=registries&view=clients"
            className="flex w-fit items-center gap-1 text-xs text-text-tertiary hover:text-text-primary"
          >
            <ArrowLeft className="size-3" />
            Clients
          </Link>
        }
        title={client.name}
        subtitle={`Everything configured about this client for ${monthOnly(month)}.${
          client.isActive ? '' : ' This client is inactive.'
        }`}
        controls={<MonthStepper month={month} onShift={onMonthShift} />}
      />

      <div className="flex flex-col gap-6 px-[var(--page-margin)] pb-8">
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Base fixed', value: budget.baseFixed },
            { label: 'Base variable', value: budget.baseVariable },
            { label: 'Supplements', value: budget.supplementFixed + budget.supplementVariable },
            { label: 'Effective total', value: budget.effectiveTotal },
          ].map((tile) => (
            <div
              key={tile.label}
              className="rounded-16 border border-border-faint bg-[var(--color-canvas-base)] p-3"
            >
              <p className="text-xs text-text-tertiary">{tile.label}</p>
              <p className="text-xl font-semibold text-text-primary">{formatHours(tile.value)}</p>
            </div>
          ))}
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-sm font-semibold text-text-primary">This month’s hours</h2>
            <p className="text-xs text-text-tertiary">
              {formatHours(capacity.attribution.loggedHours)} logged in {monthOnly(month)}, and the
              ClickUp lists or tags they came from.
            </p>
          </div>
          {capacity.attribution.loggedHours === 0 && budget.effectiveTotal > 0 && (
            <p className="text-sm text-destructive">
              This client has a budget but no hours this month. Either nobody has worked on it yet,
              or their time is landing on a list that matched another client or none.
            </p>
          )}
          <DataGrid<AttributedList> config={attributionConfig} rows={capacity.attribution.lists} />
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-sm font-semibold text-text-primary">Budget</h2>
              <p className="text-xs text-text-tertiary">
                Saving sets the budget from {monthOnly(month)} onward; earlier months keep what was
                in force then.
              </p>
            </div>
            <Button onClick={() => setEditingBudget(true)}>Set budget</Button>
          </div>
          <DataGrid<BudgetHistoryRow> config={historyConfig} rows={capacity.budgetHistory} />
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-sm font-semibold text-text-primary">Supplements</h2>
              <p className="text-xs text-text-tertiary">
                One-off hours on top of the base budget, prorated by how much of their window falls
                in the month.
              </p>
            </div>
            <Button onClick={() => setAddingSupplement(true)}>
              <Plus className="size-4" />
              Add supplement
            </Button>
          </div>
          <DataGrid<SupplementItem> config={supplementsConfig} rows={capacity.supplements} />
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-sm font-semibold text-text-primary">Assignments</h2>
            <p className="text-xs text-text-tertiary">
              Per-member shares of this client&apos;s budget.
            </p>
          </div>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!assignment.userId) return;
              void run(async () => {
                unwrapAction(
                  await onUpsertAssignment({
                    clientId: client.id,
                    userId: assignment.userId,
                    fixedHours: Number(assignment.fixed || 0),
                    variableHours: Number(assignment.variable || 0),
                    month,
                  }),
                );
                setAssignment({ userId: '', name: '', fixed: '', variable: '' });
                toast.success('Assignment saved.');
              }, 'Saving the assignment failed. Try again.');
            }}
          >
            <div className="w-56">
              <SearchableSelect
                value={assignment.userId}
                selectedLabel={assignment.name || undefined}
                ariaLabel="Assignment member"
                placeholder="Member…"
                emptyLabel="No member matches"
                // A member with a share here already is shown but not offered again.
                options={members.items.map((member) => ({
                  value: member.id,
                  label: member.name,
                  disabled: assignedStaffIds.has(member.id),
                }))}
                {...memberSearch}
                onChange={(userId) =>
                  setAssignment((p) => ({
                    ...p,
                    userId,
                    name: members.items.find((m) => m.id === userId)?.name ?? '',
                  }))
                }
              />
            </div>
            <Input
              type="number"
              min={0}
              value={assignment.fixed}
              onChange={(e) => setAssignment((p) => ({ ...p, fixed: e.target.value }))}
              placeholder="Fixed"
              aria-label="Assignment fixed hours"
              className="w-24"
            />
            <Input
              type="number"
              min={0}
              value={assignment.variable}
              onChange={(e) => setAssignment((p) => ({ ...p, variable: e.target.value }))}
              placeholder="Variable"
              aria-label="Assignment variable hours"
              className="w-24"
            />
            <Button type="submit" disabled={busy || !assignment.userId}>
              Assign
            </Button>
          </form>
          <DataGrid<AssignmentItem> config={assignmentsConfig} rows={capacity.assignments} />
        </section>

        {editingBudget && (
          <FormDialog<BudgetValues>
            open
            onOpenChange={(open) => !open && setEditingBudget(false)}
            title={`Budget for ${client.name}`}
            description={`Effective from ${monthOnly(month)}.`}
            config={budgetFormConfig}
            initial={{
              fixedHours: String(budget.baseFixed),
              variableHours: String(budget.baseVariable),
            }}
            validate={(values) => ({
              ...(Number.isFinite(Number(values.fixedHours)) && values.fixedHours !== ''
                ? {}
                : { fixedHours: 'Enter a number.' }),
              ...(Number.isFinite(Number(values.variableHours)) && values.variableHours !== ''
                ? {}
                : { variableHours: 'Enter a number.' }),
            })}
            onSubmit={async (values) => {
              unwrapAction(
                await onUpsertBudget({
                  clientId: client.id,
                  effectiveMonth: month,
                  fixedHours: Number(values.fixedHours),
                  variableHours: Number(values.variableHours),
                }),
                'Saving the budget failed. Try again.',
              );
              setEditingBudget(false);
            }}
            submitLabel="Save budget"
            pendingLabel="Saving…"
            successToast={{ title: `Budget set from ${monthOnly(month)}.` }}
          />
        )}

        <FormDialog<SupplementValues>
          open={addingSupplement}
          onOpenChange={setAddingSupplement}
          title={`Add supplement to ${client.name}`}
          config={supplementFormConfig}
          initial={{
            bucket: 'variable',
            kind: 'supplement',
            hours: '',
            startDate: '',
            endDate: '',
            notes: '',
          }}
          validate={(values) => ({
            ...(Number(values.hours) > 0 ? {} : { hours: 'Enter hours above zero.' }),
            ...(values.startDate ? {} : { startDate: 'Pick a start date.' }),
            ...(values.endDate ? {} : { endDate: 'Pick an end date.' }),
          })}
          onSubmit={async (values) => {
            unwrapAction(
              await onCreateSupplement({
                clientId: client.id,
                bucket: values.bucket,
                kind: values.kind,
                hours: Number(values.hours),
                startDate: values.startDate,
                endDate: values.endDate,
                notes: values.notes || undefined,
                month,
              }),
              'Adding the supplement failed. Try again.',
            );
          }}
          submitLabel="Add supplement"
          pendingLabel="Adding…"
          successToast={{ title: 'Supplement added.' }}
        />
      </div>
    </div>
  );
}
